import {
  ChevronDownIcon,
  HomeIcon,
  MagnifyingGlassIcon,
  TagIcon,
  XMarkIcon,
} from "@heroicons/react/24/solid";
import React, { useEffect, useMemo, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { useDebounce } from "usehooks-ts";
import { DASHBOARD_ROUTES } from "../../config/routes";
import { useAuth } from "../../hooks/useAuth";
import { SidebarIcon } from "../icons/SidebarIcons";
import { ROLES } from "../../config/constants";
import { useSelectedPlace } from "../../hooks/place/useSelectedPlace";
import {
  PartnerRequestBadge,
  usePartnerRequestAlerts,
} from "../../hooks/Partners/usePartnerRequestAlerts";
import { NewDataBadge, useNewDataAlerts } from "../../hooks/useNewDataAlerts";
import { SIDEBAR_CSS } from "../../helpers/constants";

/* ============================== routes ============================== */

type NavItem = {
  label: string;
  path: string;
  icon: React.ElementType;
  show?: boolean;
  isActive: (pathname: string) => boolean;
};

const createDashboardRoutes = (
  user: User | null,
  isAdmin: boolean,
  isEventsEnabled: boolean,
): NavItem[] => {
  if (
    user?.role?.label?.toLowerCase() === ROLES.USER.toLowerCase() &&
    user?.isPlaceManager === true
  ) {
    return [
      {
        label: "Activités",
        path: "/place-manager/activities",
        icon: SidebarIcon.ActivitiesSVG,
        show: true,
        isActive: (pathname) => pathname.includes("/place-manager/activities"),
      },
      {
        label: "Reservations",
        path: "/place-manager/bookings",
        icon: SidebarIcon.BookingSvg,
        show: true,
        isActive: (pathname) => pathname.includes("/place-manager/bookings"),
      },
      {
        label: "Commission",
        path: "/place-manager/commission",
        icon: SidebarIcon.OffersSvg,
        show: true,
        isActive: (pathname) => pathname.includes("/place-manager/commission"),
      },
    ];
  }
  if (
    user?.role?.label?.toLowerCase() === ROLES.PLACE_ADMIN.toLowerCase() &&
    user?.isMultiPlaceAdmin === true
  ) {
    return [
      {
        label: "Reservations",
        path: "/bookings",
        icon: SidebarIcon.BookingSvg,
        show: true,
        isActive: (pathname) => pathname.includes("/bookings"),
      },
      {
        label: "Offres",
        path: "/offers",
        icon: SidebarIcon.OffersSvg,
        show: true,
        isActive: (pathname) => pathname.includes("/offers"),
      },
      {
        label: "Mes Partenaires",
        path: "/place-admin-partners",
        icon: SidebarIcon.PartnersSvg,
        show: true,
        isActive: (pathname) => pathname.includes("/place-admin-partners"),
      },
      {
        label: "Établissement",
        path: "/places",
        icon: SidebarIcon.PlaceSVG,
        show: true,
        isActive: (pathname) => /^\/places\/?$/.test(pathname),
      },
      {
        label: "Inspecteurs",
        path: DASHBOARD_ROUTES.places.inspectors(user?.adminForPlace?.id ?? ""),
        icon: SidebarIcon.InspectorsSvg,
        show: true,
        isActive: (pathname) => pathname.includes("/inspectors"),
      },
      {
        label: "Factures",
        path: DASHBOARD_ROUTES.invoices.list(),
        icon: SidebarIcon.MoneyIcon,
        show: true,
        isActive: (pathname) => pathname.includes("/invoices"),
      },
    ];
  }
  return [
    {
      label: "Établissements",
      path: "/places",
      icon: SidebarIcon.PlaceSVG,
      show: true,
      isActive: (pathname) => pathname.includes("/places"),
    },
    {
      label: "Utilisateurs",
      path: "/users",
      icon: SidebarIcon.UsersSvg,
      show: isAdmin,
      isActive: (pathname) => pathname.includes("/users"),
    },
    {
      label: "Réservations",
      path: "/bookings",
      icon: SidebarIcon.BookingSvg,
      show: user?.adminForPlace?.supportReservations || isAdmin,
      isActive: (pathname) => pathname.includes("/bookings"),
    },
    {
      label: "Evénements",
      path: "/events",
      icon: SidebarIcon.EventSvg,
      show: isAdmin || isEventsEnabled,
      isActive: (pathname) => pathname.includes("/events"),
    },
    {
      label: "Offres",
      path: "/offers",
      icon: SidebarIcon.OffersSvg,
      show: true,
      isActive: (pathname) => pathname.includes("/offers"),
    },
    {
      label: "Activités",
      path: "/activities",
      icon: SidebarIcon.ActivitiesSVG,
      show: true,
      isActive: (pathname) => pathname.includes("/activities"),
    },
    {
      label: "Packs",
      path: "/packs",
      icon: SidebarIcon.PacksSvg,
      show: true,
      isActive: (pathname) => pathname.includes("/packs"),
    },
    {
      label: "Catégories",
      path: "/categories",
      icon: SidebarIcon.CategoriesSvg,
      show: isAdmin,
      isActive: (pathname) => pathname.includes("/categories"),
    },
    {
      label: "Catégories de services",
      path: "/service-categories",
      icon: SidebarIcon.ServiceCategoriesSVG,
      show: isAdmin,
      isActive: (pathname) => pathname.includes("/service-categories"),
    },
    {
      label: "Promo codes",
      path: "/promo-codes",
      icon: TagIcon,
      show: isAdmin,
      isActive: (pathname) => pathname.includes("/promo-codes"),
    },
    {
      label: "Bonus",
      path: DASHBOARD_ROUTES.reward.list(),
      icon: SidebarIcon.RewardIcon,
      show: isAdmin,
      isActive: (pathname) => pathname.includes("/reward"),
    },
    {
      label: "Publicités",
      path: "/advertisement",
      icon: SidebarIcon.AdvertisementSVG,
      show: isAdmin,
      isActive: (pathname) => pathname.includes("/advertisement"),
    },
    {
      label: "Notifications",
      path: "/notifications",
      icon: SidebarIcon.NotificationsSvg,
      show: isAdmin,
      isActive: (pathname) => pathname.includes("/notifications"),
    },
    {
      label: "Partenaires",
      path: "/partners",
      icon: SidebarIcon.PartnersSvg,
      show: isAdmin,
      isActive: (pathname) => pathname.includes("/partners"),
    },
    {
      label: "Villes",
      path: "/cities",
      icon: SidebarIcon.CitiesSvg,
      show: isAdmin,
      isActive: (pathname) => pathname.includes("/cities"),
    },
    {
      label: "Inspecteurs",
      path: "/inspectors",
      icon: SidebarIcon.InspectorsSvg,
      show: isAdmin,
      isActive: (pathname) => pathname.includes("/inspectors"),
    },
    {
      label: "Newsletter",
      path: "/newsletter",
      icon: SidebarIcon.NewsletterSvg,
      show: isAdmin,
      isActive: (pathname) => pathname.includes("/newsletter"),
    },
    {
      label: "Variables",
      path: "/variables",
      icon: SidebarIcon.ArrowsRightLeftIcon,
      show: isAdmin,
      isActive: (pathname) => pathname.includes("/variables"),
    },
    {
      label: "Paiements",
      path: "/payouts",
      icon: SidebarIcon.PayoutsSvg,
      show: true,
      isActive: (pathname) => pathname.includes("/payouts"),
    },
    {
      label: "Traductions",
      path: "/translations",
      icon: SidebarIcon.TranslationsSvg,
      show: isAdmin,
      isActive: (pathname) => pathname.includes("/translations"),
    },
  ];
};

const createVenezTaxiRoutes = (isAdmin: boolean): NavItem[] => [
  {
    label: "Chauffeurs",
    path: "/drivers",
    icon: SidebarIcon.DriversSvg,
    show: isAdmin,
    isActive: (pathname) => pathname.includes("/drivers"),
  },
  {
    label: "Sessions",
    path: "/Sessions",
    icon: SidebarIcon.SessionSVG,
    show: isAdmin,
    isActive: (pathname) => pathname.includes("/Sessions"),
  },
  {
    label: "Demandes",
    path: "/demandes",
    icon: SidebarIcon.RequestDriverSVG,
    show: isAdmin,
    isActive: (pathname) => pathname.includes("/demandes"),
  },
  {
    label: "Trajets",
    path: "/rides",
    icon: SidebarIcon.RidesSVG,
    show: isAdmin,
    isActive: (pathname) => pathname.includes("/rides"),
  },
  {
    label: "Taxis",
    path: "/taxi",
    icon: SidebarIcon.TaxisSVG,
    show: isAdmin,
    isActive: (pathname) => pathname === "/taxi",
  },
  {
    label: "Zones spéciales",
    path: "/taxi/special-zones",
    icon: SidebarIcon.SpecialZoneSvg,
    show: isAdmin,
    isActive: (pathname) => pathname.includes("/special-zones"),
  },
  {
    label: "Transactions",
    path: "/transactions",
    icon: SidebarIcon.ArrowsRightLeftIcon,
    show: isAdmin,
    isActive: (pathname) => pathname.includes("/transactions"),
  },
  {
    label: "Coupons",
    path: DASHBOARD_ROUTES.taxi.discounts.list(),
    icon: SidebarIcon.OffersSvg,
    show: isAdmin,
    isActive: (pathname) => pathname.includes("/taxi/discounts"),
  },
  {
    label: "Statistiques",
    path: "/statistics",
    icon: SidebarIcon.SatistiquesIconSVG,
    show: isAdmin,
    isActive: (pathname) => pathname.includes("/statistics"),
  },
  {
    label: "Paramètres",
    path: "/settings",
    icon: SidebarIcon.SettingsSVG,
    show: isAdmin,
    isActive: (pathname) => pathname.includes("/settings"),
  },
];

/* ============================== component ============================== */

interface SidebarProps {
  onClose?: () => void;
  className?: string;
}

export default function Sidebar({ onClose, className = "" }: SidebarProps) {
  const { isAdmin, user } = useAuth();
  const { hasNew, markSeen } = useNewDataAlerts(isAdmin);
  const location = useLocation();
  const pathname = location.pathname;

  const [searchTerm, setSearchTerm] = useState("");
  const searchQuery = useDebounce(searchTerm, 300).trim().toLowerCase();
  const [open, setOpen] = useState<Record<string, boolean>>({
    "Tableau de bord": true,
  });

  const { selectedPlace } = useSelectedPlace(user, isAdmin);
  const { total: requestTotal, hasNew: hasNewRequests } =
    usePartnerRequestAlerts(isAdmin);

  const isEventsEnabled = selectedPlace?.isEventsEnabled;

  const adminRoutes = useMemo(
    () => createDashboardRoutes(user, isAdmin, isEventsEnabled),
    [user, isAdmin, isEventsEnabled],
  );

  const sections = useMemo(
    () =>
      [
        { title: "Tableau de bord", items: adminRoutes },
        { title: "Venez Taxi", items: createVenezTaxiRoutes(isAdmin) },
      ].map((s) => ({ ...s, items: s.items.filter((i) => i.show) })),
    [adminRoutes, isAdmin],
  );

  const filteredSections = useMemo(() => {
    if (!searchQuery) return sections.filter((s) => s.items.length > 0);
    return sections
      .map((s) => ({
        ...s,
        items: s.items.filter((i) =>
          i.label.toLowerCase().includes(searchQuery),
        ),
      }))
      .filter((s) => s.items.length > 0);
  }, [sections, searchQuery]);

  // Open the section that contains the current page
  useEffect(() => {
    const current = sections.find((s) =>
      s.items.some((i) => i.isActive(pathname)),
    );
    if (current)
      setOpen((o) => (o[current.title] ? o : { ...o, [current.title]: true }));
  }, [pathname, sections]);

  // Clear the "new data" dot for the page being viewed
  useEffect(() => {
    sections.forEach((s) =>
      s.items.forEach((i) => {
        if (i.isActive(pathname)) markSeen(i.path);
      }),
    );
  }, [pathname, markSeen, sections]);

  /* ----- render helpers (plain functions, not components, so the search input keeps focus) ----- */

  const renderItem = (item: NavItem, withBadges: boolean) => {
    const active = item.isActive(pathname);
    return (
      <li key={item.label} className="list-unstyled">
        <Link
          to={item.path}
          onClick={onClose}
          className={`vz-nav-item ${active ? "is-active" : ""}`}
          aria-current={active ? "page" : undefined}
        >
          <span className="vz-nav-icon" aria-hidden>
            {React.createElement(item.icon, { className: "w-100 h-100" })}
          </span>
          <span className="vz-nav-label">{item.label}</span>
          {withBadges &&
            (item.path === "/partners" ? (
              <PartnerRequestBadge
                total={requestTotal}
                hasNew={hasNewRequests}
              />
            ) : (
              hasNew(item.path) && <NewDataBadge />
            ))}
        </Link>
      </li>
    );
  };

  const homeActive = pathname.includes("/home");

  const renderAdmin = () => (
    <>
      <div className="vz-search mb-3 px-1">
        <MagnifyingGlassIcon
          className="vz-search-icon"
          width={16}
          aria-hidden
        />
        <input
          type="search"
          name="search"
          placeholder="Rechercher"
          aria-label="Rechercher dans le menu"
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
        />
        {searchTerm && (
          <button
            type="button"
            className="vz-search-clear"
            aria-label="Effacer la recherche"
            onClick={() => setSearchTerm("")}
          >
            <XMarkIcon width={16} />
          </button>
        )}
      </div>

      {!searchQuery && (
        <ul className="p-0 mb-3">
          <li className="list-unstyled">
            <Link
              to="/home"
              onClick={onClose}
              className={`vz-nav-item ${homeActive ? "is-active" : ""}`}
              aria-current={homeActive ? "page" : undefined}
            >
              <span className="vz-nav-icon" aria-hidden>
                <HomeIcon />
              </span>
              <span className="vz-nav-label">Accueil</span>
            </Link>
          </li>
        </ul>
      )}

      {filteredSections.length === 0 && (
        <p className="text-muted px-3 mb-0" style={{ fontSize: 13 }}>
          Aucun résultat pour « {searchTerm} »
        </p>
      )}

      {filteredSections.map((section) => {
        const isOpen = searchQuery ? true : !!open[section.title];
        return (
          <div key={section.title} className="mb-2">
            <button
              type="button"
              className="vz-section-btn"
              aria-expanded={isOpen}
              onClick={() =>
                setOpen((o) => ({ ...o, [section.title]: !o[section.title] }))
              }
            >
              <span>{section.title}</span>
              <ChevronDownIcon
                width={14}
                className={`vz-chevron ${isOpen ? "is-open" : ""}`}
                aria-hidden
              />
            </button>
            {isOpen && (
              <ul className="p-0 mt-1 mb-0 d-flex flex-column gap-1">
                {section.items.map((item) => renderItem(item, true))}
              </ul>
            )}
          </div>
        );
      })}
    </>
  );

  const renderPlaceAdmin = () => (
    <ul className="p-0 mb-0 d-flex flex-column gap-1">
      {adminRoutes.filter((i) => i.show).map((item) => renderItem(item, false))}
    </ul>
  );

  return (
    <nav
      className={`sidebar px-2 py-3 ${className}`}
      aria-label="Navigation principale"
    >
      <style>{SIDEBAR_CSS}</style>
      {isAdmin ? renderAdmin() : renderPlaceAdmin()}
    </nav>
  );
}
